import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { DoorOpen, Pencil, Plus } from "lucide-react";

import { hostelService, type Bed, type Hostel, type Room } from "../../services/hostelService";
import { apiErrorMessage } from "../../utils/apiError";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { errorLine } from "../inventory/InventoryParts";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { ActionPanel, DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { BED_TYPES, ROOM_CONDITIONS, ROOM_TYPES, bedStatus, labelFrom, nextBedLabel, roomConfig, roomStatus } from "./hostelLogic";

function RoomForm({ room, hostels, rooms, onClose, onDone }: { room?: Room; hostels: Hostel[]; rooms: Room[]; onClose: () => void; onDone: (row: Room) => void }) {
  const [hostelId, setHostelId] = useState(room ? String(room.hostel) : hostels.length === 1 ? String(hostels[0].id) : "");
  const hostel = hostels.find((row) => String(row.id) === hostelId);
  const configured = rooms.filter((row) => row.hostel === hostel?.id && row.id !== room?.id).reduce((sum, row) => sum + row.capacity, 0);
  const left = hostel ? hostel.capacity - configured : 0;
  const save = useMutation({
    mutationFn: (data: Record<string, unknown>) => (room ? hostelService.updateRoom({ id: room.id, data }) : hostelService.createRoom(data)),
    onSuccess: onDone,
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const text = (name: string) => String(form.get(name) ?? "").trim();
    save.mutate({
      hostel: Number(hostelId),
      room_number: text("room_number"),
      room_type: text("room_type"),
      capacity: Number(text("capacity")),
      floor: Number(text("floor") || 0),
      condition: text("condition"),
      has_private_bathroom: form.get("bathroom") === "on",
      maintenance_notes: text("notes") || null,
      ...(room ? { is_active: form.get("in_use") === "on" } : { is_active: true }),
    });
  };
  return (
    <FormModal
      id="room"
      title={room ? `Edit room ${room.room_number}` : "Add a room"}
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The room could not be saved.") : undefined}
      saveLabel={room ? "Save changes" : "Add the room"}
      note={room ? undefined : "Add its beds from the room once it is saved."}
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="The room" divided={false}>
        <Field label="Residence" hint={hostel ? `${left} of its ${hostel.capacity} beds not yet in a room.` : undefined}>
          <select required disabled={Boolean(room)} value={hostelId} onChange={(event) => setHostelId(event.target.value)} className="input mt-1">
            <option value="">Choose a residence</option>
            {hostels.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
          </select>
        </Field>
        <Field label="Room name or number" hint="As on the door, e.g. G4, Kilimanjaro.">
          <input required name="room_number" maxLength={20} defaultValue={room?.room_number} className="input mt-1" />
        </Field>
        <Field label="Kind">
          <select name="room_type" defaultValue={room?.room_type ?? "dormitory"} className="input mt-1">
            {ROOM_TYPES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
        <Field label="Beds" hint={room ? `At least ${Math.max(room.current_occupancy, 1)}, the boarders now.` : undefined}>
          <input required type="number" name="capacity" min={Math.max(1, room?.current_occupancy ?? 1)} max={hostel ? Math.max(left, room?.capacity ?? 0) : undefined} step={1} defaultValue={room?.capacity ?? 8} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Floor" hint="0 for the ground floor.">
          <input type="number" name="floor" min={0} step={1} defaultValue={room?.floor ?? 0} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Condition">
          <select name="condition" defaultValue={room?.condition ?? "good"} className="input mt-1">
            {ROOM_CONDITIONS.filter(([key]) => key !== "under_repair" || room?.condition === "under_repair").map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <input type="checkbox" name="bathroom" defaultChecked={room?.has_private_bathroom} className="h-4 w-4 rounded border-gray-300" /> Its own bathroom
        </label>
        {room && (
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <input type="checkbox" name="in_use" defaultChecked={room.is_active} className="h-4 w-4 rounded border-gray-300" /> In use
          </label>
        )}
        <Field label="Notes" optional className="sm:col-span-2">
          <textarea name="notes" rows={2} defaultValue={room?.maintenance_notes ?? ""} className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** Every room, its beds, and who sleeps where. */
export function RoomsPanel({ rooms, beds, hostels, isLoading, isError, onRetry, canManage, onChanged }: {
  rooms?: Room[];
  beds: Bed[];
  hostels: Hostel[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canManage: boolean;
  onChanged: () => void;
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [editing, setEditing] = useState<Room | "new" | null>(null);
  const [bedType, setBedType] = useState("bunk_bottom");
  const [bedForm, setBedForm] = useState(0);
  // Beds just added, until the list refreshes, so the next label moves on.
  const [justAdded, setJustAdded] = useState<{ room: number; label: string }[]>([]);
  const addBed = useMutation({
    mutationFn: hostelService.createBed,
    onSuccess: (bed) => {
      setJustAdded((current) => [...current, { room: bed.room, label: bed.bed_number }]);
      setBedForm((count) => count + 1);
      setNotice({ tone: "success", text: `Bed ${bed.bed_number} added.` });
      onChanged();
    },
  });
  const toggleBed = useMutation({ mutationFn: hostelService.updateBed, onSuccess: () => onChanged() });

  const columns: RegisterColumn<Room>[] = [
    { header: "Residence", cell: (row) => row.hostel_name, exportValue: (row) => row.hostel_name, className: "text-gray-700" },
    { header: "Room", cell: (row) => <span className="font-medium text-gray-900">{row.room_number}</span>, exportValue: (row) => row.room_number },
    { header: "Floor", cell: (row) => (row.floor === 0 ? "Ground" : row.floor), exportValue: (row) => row.floor, className: "text-gray-700" },
    { header: "Kind", cell: (row) => labelFrom(ROOM_TYPES, row.room_type), exportValue: (row) => labelFrom(ROOM_TYPES, row.room_type), className: "text-gray-700" },
    { header: "Beds taken", cell: (row) => `${row.current_occupancy} / ${row.capacity}`, exportValue: (row) => row.current_occupancy, align: "right" },
    { header: "Who sleeps here", cell: (row) => row.occupants.map((person) => person.name).join(", ") || <span className="text-gray-300">—</span>, exportValue: (row) => row.occupants.map((person) => person.name).join("; "), className: "min-w-[14rem] max-w-md whitespace-normal text-gray-700" },
    { header: "Condition", cell: (row) => labelFrom(ROOM_CONDITIONS, row.condition), exportValue: (row) => labelFrom(ROOM_CONDITIONS, row.condition), className: "text-gray-700" },
    { header: "Status", cell: (row) => <StatusDot {...roomStatus(row)} />, exportValue: (row) => roomStatus(row).label },
  ];

  const room = viewing !== null ? rooms?.find((row) => row.id === viewing) : undefined;
  const roomBeds = room ? beds.filter((bed) => bed.room === room.id).sort((a, b) => a.bed_number.localeCompare(b.bed_number, undefined, { numeric: true })) : [];

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title="Rooms"
        description={canManage ? "Open a room to see its beds, add beds or take one out of use." : "Rooms, beds and who sleeps where."}
        actions={canManage && (
          <button type="button" disabled={!hostels.length} onClick={() => setEditing("new")} className="btn-primary h-8 min-h-8 px-3 text-xs disabled:opacity-50">
            <DoorOpen className="mr-1.5 h-3.5 w-3.5" /> Add a room
          </button>
        )}
        rows={rooms}
        config={roomConfig}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => {
          addBed.reset();
          toggleBed.reset();
          setViewing(row.id);
        }}
        noun={["room", "rooms"]}
        exportName={{ file: "boarding-rooms", title: "Boarding Rooms" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={canManage ? (hostels.length ? "No rooms yet. Add the first with Add a room." : "Add a residence first, then its rooms.") : "No rooms to show."}
      />

      {room && !editing && (
        <DetailsModal
          title={`${room.hostel_name} · room ${room.room_number}`}
          headline={{ label: "Beds taken", value: `${room.current_occupancy} of ${room.capacity}`, note: `${labelFrom(ROOM_TYPES, room.room_type)} · ${room.floor === 0 ? "ground floor" : `floor ${room.floor}`}` }}
          status={roomStatus(room)}
          facts={[
            ["Condition", labelFrom(ROOM_CONDITIONS, room.condition)],
            ["Beds set up", `${roomBeds.length} of ${room.capacity}`],
            ["Own bathroom", room.has_private_bathroom ? "Yes" : "No"],
            ["Last repaired", room.last_maintenance ?? undefined],
          ]}
          sections={[{ title: "Notes", body: room.maintenance_notes }]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {canManage && (
                <button type="button" className="btn btn-primary gap-1.5" onClick={() => setEditing(room)}>
                  <Pencil className="h-4 w-4" /> Edit
                </button>
              )}
            </>
          }
        >
          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Beds</h4>
            {roomBeds.length ? (
              <div className="sheet-wrap mt-1.5">
                <table className="sheet">
                  <thead>
                    <tr>
                      <th>Bed</th>
                      <th>Kind</th>
                      <th>Who sleeps here</th>
                      <th>Status</th>
                      {canManage && <th className="text-right">Action</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {roomBeds.map((bed) => (
                      <tr key={bed.id}>
                        <td className="font-medium text-gray-900">{bed.bed_number}</td>
                        <td className="text-gray-700">{labelFrom(BED_TYPES, bed.bed_type)}</td>
                        <td className="text-gray-700">{bed.occupant?.name ?? (bed.is_occupied ? "Taken" : "—")}</td>
                        <td><StatusDot {...bedStatus(bed)} /></td>
                        {canManage && (
                          <td className="text-right">
                            {!bed.is_occupied && !bed.is_reserved && (
                              <button
                                type="button"
                                disabled={toggleBed.isPending}
                                onClick={() => toggleBed.mutate({ id: bed.id, data: { is_under_maintenance: !bed.is_under_maintenance } })}
                                className="rounded px-2 py-0.5 text-xs font-medium text-primary-700 hover:bg-primary-50"
                              >
                                {bed.is_under_maintenance ? "Back in use" : "Out of use"}
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="mt-1.5 text-sm text-gray-500">No beds set up in this room yet.</p>
            )}
            {toggleBed.isError && <div className="mt-2">{errorLine(toggleBed.error, "The bed could not be changed.")}</div>}
          </section>

          {canManage && roomBeds.length < room.capacity && (
            <ActionPanel title="Add a bed" description={`${room.capacity - roomBeds.length} more can be set up in this room.`}>
              <form
                key={bedForm}
                onSubmit={(event: FormEvent<HTMLFormElement>) => {
                  event.preventDefault();
                  const label = String(new FormData(event.currentTarget).get("bed_number") ?? "").trim();
                  addBed.mutate({ room: room.id, bed_number: label, bed_type: bedType, condition: "good" });
                  if (bedType === "bunk_bottom") setBedType("bunk_top");
                  else if (bedType === "bunk_top") setBedType("bunk_bottom");
                }}
                className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end"
              >
                <Field label="Bed label">
                  <input required name="bed_number" maxLength={10} defaultValue={nextBedLabel([...roomBeds, ...justAdded.filter((entry) => entry.room === room.id).map((entry) => ({ bed_number: entry.label }))])} className="input mt-1" />
                </Field>
                <Field label="Kind">
                  <select value={bedType} onChange={(event) => setBedType(event.target.value)} className="input mt-1">
                    {BED_TYPES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                  </select>
                </Field>
                <button disabled={addBed.isPending} className="btn btn-primary gap-1.5">
                  <Plus className="h-4 w-4" /> Add the bed
                </button>
              </form>
              {addBed.isError && errorLine(addBed.error, "The bed could not be added.")}
            </ActionPanel>
          )}
        </DetailsModal>
      )}

      {editing && (
        <RoomForm
          room={editing === "new" ? undefined : editing}
          hostels={hostels.filter((row) => row.is_active || (editing !== "new" && row.id === editing.hostel))}
          rooms={rooms ?? []}
          onClose={() => setEditing(null)}
          onDone={(saved) => {
            const created = editing === "new";
            setEditing(null);
            setNotice({ tone: "success", text: created ? `Room ${saved.room_number} added. Add its beds now.` : `Room ${saved.room_number} updated.` });
            setViewing(saved.id);
            onChanged();
          }}
        />
      )}
    </div>
  );
}
